import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { TableModule, TableLazyLoadEvent } from 'primeng/table';
import { Button } from '../../Components/button/button';
import { PaginatorState } from 'primeng/paginator';
import { ITEMS_PER_PAGE } from '../../core/config/pagination.constants';
import { SpinnerOverlayService } from '../../Components/spinner/spinner-overlay.service';
import { OrderService } from '../../orders/order-service';
import { environment } from '../../../../environment';
import { OrderDetailDialog } from '../../orders/order-detail-dialog/order-detail-dialog';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-sales',
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    FormsModule,
    Button
  ],
  templateUrl: './sales.html',
  styleUrl: './sales.css'
})
export class Sales implements OnInit {
  filters = {
    asesor: '',
    cliente: '',
    bodega: '',
    fechaDesde: '',
    fechaHasta: ''
  };

  selectedValues = {
    clienteAN: null as number | null,
    bodegaCode: null as string | null,
    asesorAN: null as number | null
  };

  allData = {
    clientes: [] as any[],
    bodegas: [] as any[],
    asesores: [] as any[]
  };

  suggestions = {
    cliente: [] as any[],
    bodega: [] as any[],
    asesor: [] as any[]
  };

  showDropdown = {
    cliente: false,
    bodega: false,
    asesor: false
  };

  flowFromAsesor = false;
  flowFromCliente = false;
  flowFromBodega = false;

  salesList: any[] = [];
  totalItems: number = 0;
  rows = ITEMS_PER_PAGE;
  first = 0;
  page = 0;

  constructor(
    private orderService: OrderService,
    private spinner: SpinnerOverlayService,
    private http: HttpClient,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    this.findAll();
    this.loadInitialFiltersData();
  }

  loadInitialFiltersData() {
    const baseUrl = environment.wsApiBaseUrl;

    this.http.get<any>(`${baseUrl}/api/customer?nameAccountManager=`).subscribe({
      next: (res) => this.allData.asesores = res.accountManagers || []
    });

    this.http.get<any>(`${baseUrl}/api/customer?name=`).subscribe({
      next: (res) => this.allData.clientes = res.customer || res.customers || []
    });

    this.http.get<any>(`${baseUrl}/api/warehouse?name=`).subscribe({
      next: (res) => this.allData.bodegas = res.warehouses || []
    });
  }

  onFocus(type: 'cliente' | 'bodega' | 'asesor') {

    if (type === 'bodega' && this.flowFromAsesor && !this.selectedValues.clienteAN) {
      return;
    }

    this.showDropdown[type] = true;
    this.filterLocalData(type);
  }

  onBlur(type: 'cliente' | 'bodega' | 'asesor') {
    setTimeout(() => {
      this.showDropdown[type] = false;
    }, 200);
  }

  onSearchInput(type: 'cliente' | 'bodega' | 'asesor') {
    const term = this.filters[type];

    if (!term) {

      if (type === 'asesor') {
        this.selectedValues.asesorAN = null;
        this.flowFromAsesor = false;
        this.loadAllClientes();
      }

      if (type === 'cliente' && !term) {
        this.selectedValues.clienteAN = null;
        this.flowFromCliente = false;
        this.loadAllBodegas();
      }

      if (type === 'bodega' && !term) {
        this.selectedValues.bodegaCode = null;
        this.flowFromBodega = false;
      }
    }

    this.showDropdown[type] = true;
    this.filterLocalData(type);
  }

  onDateChange() {
    this.findAll(true);
  }

  filterLocalData(type: 'cliente' | 'bodega' | 'asesor') {
    const term = this.filters[type]?.toLowerCase() || '';

    if (type === 'cliente') {
      this.suggestions.cliente = this.allData.clientes.filter(item =>
        item.name?.toLowerCase().includes(term) || item.ruc?.includes(term)
      );
    } else if (type === 'bodega') {
      this.suggestions.bodega = this.allData.bodegas.filter(item =>
        item.name?.toLowerCase().includes(term) || item.code?.toLowerCase().includes(term)
      );
    } else if (type === 'asesor') {
      this.suggestions.asesor = this.allData.asesores.filter(item =>
        item.name?.toLowerCase().includes(term)
      );
    }
  }

  selectSuggestion(type: 'cliente' | 'bodega' | 'asesor', item: any) {

    const baseUrl = environment.wsApiBaseUrl;

    this.filters[type] = item.name;
    this.showDropdown[type] = false;

    if (type === 'asesor') {

      this.flowFromAsesor = true;
      this.selectedValues.asesorAN = item.an;

      this.filters.cliente = '';
      this.selectedValues.clienteAN = null;

      this.filters.bodega = '';
      this.selectedValues.bodegaCode = null;

      this.http.get<any>(`${baseUrl}/api/customer?anAccountManager=${item.an}`)
        .subscribe(res => {

          this.allData.clientes = res.customers || res.customer || [];

          // si el asesor no tiene clientes
          if (this.allData.clientes.length === 0) {
            this.selectedValues.clienteAN = null;
          }

          // ejecutar búsqueda de ventas inmediatamente
          this.findAll(true);

        });
    }

    if (type === 'cliente') {

      this.selectedValues.clienteAN = item.an;

      if (!this.selectedValues.asesorAN) {
        this.flowFromCliente = true;
      }

      if (this.flowFromAsesor) {
        this.filters.bodega = '';
        this.selectedValues.bodegaCode = null;
      }

      this.http.get<any>(`${baseUrl}/api/warehouse?customer_an=${item.an}`)
        .subscribe(res => {
          this.allData.bodegas = res.warehouses || [];
        });
    }

    if (type === 'bodega') {
      this.selectedValues.bodegaCode = item.code;

      if (!this.selectedValues.asesorAN && !this.selectedValues.clienteAN) {
        this.flowFromBodega = true;
      }
    }

    this.findAll(true);
  }

  loadAllClientes() {
    this.http.get<any>(`${environment.wsApiBaseUrl}/api/customer?name=`).subscribe(res => this.allData.clientes = res.customer || res.customers || []);
  }

  loadAllBodegas() {
    this.http.get<any>(`${environment.wsApiBaseUrl}/api/warehouse?name=`).subscribe(res => this.allData.bodegas = res.warehouses || []);
  }

  findAll(reset: boolean = false) {
    if (reset) {
      this.page = 0;
      this.first = 0;
    }

    this.spinner.show();

    const params: any = {
      page: this.page,
      size: this.rows,
      sort: 'createdDate,desc',
      status: 'CONFIRMED'
    };

    if (this.selectedValues.clienteAN) params.customerAN = this.selectedValues.clienteAN;
    if (this.selectedValues.bodegaCode) params.warehouseCode = this.selectedValues.bodegaCode;
    if (this.selectedValues.asesorAN) params.anAccountManager = this.selectedValues.asesorAN;
    if (this.filters.fechaDesde) params.dateFrom = this.filters.fechaDesde;
    if (this.filters.fechaHasta) params.dateTo = this.filters.fechaHasta;

    this.orderService.findByParams(params).subscribe({
      next: (response: any) => {
        this.salesList = response.body?.order?.order || [];
        this.totalItems = response.body?.order?.totalCount || 0;
        this.spinner.hide();
      },
      error: (err: any) => {
        console.error(err);
        this.spinner.hide();
      }
    });
  }

  openOrderDetailDialog(sale: any) {
    if (!sale) return;

    this.dialog.open(OrderDetailDialog, {
      width: '80vw',
      maxWidth: '1300px',
      data: sale,
      panelClass: 'lexend-dialog'
    });
  }

  onPageChange(event: PaginatorState) {
    this.first = event.first ?? 0;
    this.rows = event.rows ?? 10;
    this.page = Math.floor((this.first ?? 0) / (this.rows ?? 10));
    this.findAll();
  }

  loadSales(event: TableLazyLoadEvent) {
    this.page = Math.floor((event.first ?? 0) / (event.rows ?? 10));
    this.rows = event.rows ?? 10;
    this.findAll();
  }

  cleanFilter() {

    this.flowFromAsesor = false;
    this.flowFromBodega = false;
    this.flowFromCliente = false;

    this.filters = {
      asesor: '',
      cliente: '',
      bodega: '',
      fechaDesde: '',
      fechaHasta: ''
    };

    this.selectedValues = {
      clienteAN: null,
      bodegaCode: null,
      asesorAN: null
    };

    this.loadInitialFiltersData();
    this.findAll(true);
  }

  getBackgroundClasses(status: string) {
    const s = (status ?? '').toString().toUpperCase();
    switch (s) {
      case 'CONFIRMED': return 'bg-green-100 text-green-800';
      case 'DRAFT': return 'bg-rose-100 text-rose-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  }

  getStatusLabel(status: string) {
    const s = (status ?? '').toString().toUpperCase();
    switch (s) {
      case 'CONFIRMED': return 'Confirmado';
      case 'DRAFT': return 'Borrador';
      default: return status ?? '-';
    }
  }
}
